import "vue-multiselect/dist/vue-multiselect.min.css";

import cloneDeep from "lodash.clonedeep";
import { Component, Vue, Watch } from "vue-property-decorator";
import Multiselect from "vue-multiselect";
import CardHeader from "@/components/Includes/CardHeader/CardHeader.vue";
import Loader from "@/components/Includes/Loader/Loader";
import { namespace, Action, Getter } from "vuex-class";

import { PrismEditor } from "vue-prism-editor";
import "vue-prism-editor/dist/prismeditor.min.css"; // import the styles somewhere

// @ts-ignore
import { highlight, languages } from "prismjs/components/prism-core";
import "prismjs/components/prism-clike";
import "prismjs/components/prism-markup";
import "prismjs/themes/prism-tomorrow.css"; // import syntax highlighting styles
import IMailSubject from "@/interfaces/IMailSubject";
import IPayloadQuery from "@/interfaces/IPayloadQuery";
import IClientApp from "@/interfaces/IClientApp";
import IMultiSelectItem from "@/interfaces/selects/IMultiSelectItem";
import { transformMultiSelect } from "@/libs/transformData/transformMultiSelect";

const mailSubjectModule = namespace("mailSubject");

@Component({
  components: {
    Loader,
    CardHeader,
    PrismEditor,
    Multiselect,
  },
})
export default class MailSubject extends Vue {
  @Action private clientAppsList!: (query: IPayloadQuery) => Promise<void>;

  @Getter private getClientApps!: () => IClientApp[];

  @mailSubjectModule.Action
  private readonly fetchMailSubject!: (params: {
    id: number;
  }) => Promise<IMailSubject>;

  @mailSubjectModule.Action("updateMailSubject")
  private readonly updateMailSubjectAction!: (
    params: IMailSubject
  ) => Promise<IMailSubject>;

  @mailSubjectModule.Action("createMailSubject")
  private readonly createMailSubjectAction!: (
    params: IMailSubject
  ) => Promise<IMailSubject>;

  private isLoading = false;
  private form: null | IMailSubject = null;

  private get isCreate(): boolean {
    return this.$route.name === "mail_subject_create";
  }

  private get clientApps(): IMultiSelectItem[] {
    return transformMultiSelect(this.getClientApps(), "title", "id");
  }

  private get clientApp() {
    return (
      this.clientApps.find(
        ({ value }) =>
          !!this.form && `/api/vpn/client_apps/${value}` === this.form.clientApp
      ) || null
    );
  }

  private set clientApp(ca) {
    if (this.form && ca) {
      this.form.clientApp = `/api/vpn/client_apps/${ca.value}`;
    }
  }

  private get mailSubjectId() {
    return +this.$route.params.id;
  }

  private async createMailSubject() {
    if (this.form) {
      this.isLoading = true;
      try {
        await this.createMailSubjectAction(cloneDeep(this.form));
        this.isLoading = false;

        this.$notify({
          group: "notice",
          title: "Create",
          text: "Successfully",
          type: "success",
        });

        this.$router.push({ name: "mail_subjects" });
      } catch {
        this.isLoading = false;
      }
    }
  }

  private async updateMailSubject() {
    if (this.form) {
      this.isLoading = true;
      try {
        await this.updateMailSubjectAction(cloneDeep(this.form));
        this.isLoading = false;

        this.$notify({
          group: "notice",
          title: "Update",
          text: "Successfully",
          type: "success",
        });
      } catch {
        this.isLoading = false;
      }
    }
  }

  private cancelForm() {
    this.$router.push({ name: "mail_subjects" });
  }

  private highlighter(code: string) {
    return highlight(code, languages.html);
  }

  private async created() {
    this.isLoading = true;
    if (!this.isCreate) {
      try {
        const [mailSubject] = await Promise.all([
          this.fetchMailSubject({
            id: this.mailSubjectId,
          }),

          this.clientAppsList({ query: "?pagination=false" }),
        ]);
        this.form = mailSubject;

        this.isLoading = false;
      } catch {
        this.isLoading = false;
      }
    } else {
      this.isLoading = true;
      try {
        await this.clientAppsList({ query: "?pagination=false" });
        this.isLoading = false;
      } catch {
        this.isLoading = false;
      }
      this.form = {
        id: 0,
        clientApp: "",
        type: "",
        title: "",
        body: "",
      };
    }
  }
}
